import { LinkButton } from "ao/components/Core";
import { useSiteInfoState } from "ao/state/siteInfo";
import React from "react";
import { messages, NavLogoKind } from "./types";

interface INavLogoProps {
	readonly navLogoKind: NavLogoKind;
	readonly tooltip: string;
	readonly selected?: boolean;
	readonly width?: boolean;
	readonly isMobile?: boolean;
}

export const NavLogo: React.FC<INavLogoProps> = ({ navLogoKind, selected, tooltip, isMobile }) => {
	const { LogoSvg, href } = NavLogoKindMetadata[navLogoKind];

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	let link = "";

	//override link dictionary if config site exists. Eventually we'll get rid of this component when public sites released there generic one
	switch (navLogoKind) {
		case NavLogoKind.Showroom:
			link = siteInfo.showroomUrl;
			break;
		case NavLogoKind.OpenDotEpic:
			link = siteInfo.openEpicUrl ?? href;
			break;
		case NavLogoKind.UserWeb:
			link = siteInfo.userWebUrl ?? href;
			break;
		case NavLogoKind.Epic:
			link = siteInfo.epicDotComUrl ?? href;
			break;
		case NavLogoKind.EpicShare:
			link = siteInfo.epicShareUrl ?? href;
			break;
		case NavLogoKind.MyChart:
			link = siteInfo.myChartUrl ?? href;
			break;
		case NavLogoKind.Cosmos:
			link = siteInfo.cosmosUrl ?? href;
			break;
		default:
			link = href;
	}

	return (
		<LinkButton
			width={isMobile ? "100%" : "unset"}
			height="36px"
			title={tooltip}
			url={link}
			id={`nav_logo_${link}`}
			paddingX="12px"
			background={selected ? "showroomPrimary.500" : "none"}
			borderRadius="0px"
			fill={selected ? "white" : "#6b615c"}
			_hover={selected ? {} : { backgroundColor: "#eae5e1", fill: "showroomPrimary.500" }}
		>
			<LogoSvg tooltip={tooltip} pathClasses={``} />
		</LinkButton>
	);
};

export const topItems = [
	{
		navLogoKind: NavLogoKind.Epic,
		tooltip: messages.layout.epicLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.EpicShare,
		tooltip: messages.layout.epicshareLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.EpicResearch,
		tooltip: messages.layout.epicresearchLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.Cosmos,
		tooltip: messages.layout.cosmosLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.MyChart,
		tooltip: messages.layout.mychartLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.OpenDotEpic,
		tooltip: messages.layout.openDotEpicLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.UserWeb,
		tooltip: messages.layout.userwebLogoTooltip.value,
	},
	{
		navLogoKind: NavLogoKind.Showroom,
		tooltip: messages.layout.showroomLogoTooltip.value,
		selected: true,
	},
];

interface IBigEpicLogoProps {}

export const BigEpicLogo: React.FC<IBigEpicLogoProps> = () => {
	const svgClasses = "group w-12 md:w-24";
	const pathClasses = "fill-epic-nav-standalone-epic-logo-back-color";
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 -1 39.14 14.84" className={svgClasses}>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<path d="M28.69 0H25.3l-.43 2.05h3.39L28.69 0z" className={pathClasses} />
				<path
					d="M33.64 4.92c1.29 0 1.9.44 1.98 1.21h3.51c.12-2.54-1.94-3.45-5.06-3.45s-5.67 1.16-6.7 3.51l.67-3.16h-3.39l-.62 2.94c-.15-2.04-1.7-3.16-4.19-3.16-1.37 0-2.68.5-3.59 1.37h-.03l.24-1.15h-3.29l-1.32 6.2H4.32l.36-1.94h6.72l.5-2.37H5.18l.36-1.91h7.63l.52-2.46H2.27L0 11.7h11.33l-.67 3.13h3.39l.83-3.92h.03c.44.64 1.49 1.13 2.99 1.13 2.57 0 4.56-1.33 5.5-3.16l-.6 2.81h3.39l.66-3.12c.02 2.36 1.9 3.47 5.21 3.47 3.73 0 5.71-1.36 6.76-3.75h-3.58C34.76 9.3 33.83 9.8 32.6 9.8c-1.63 0-2.37-.88-2.04-2.45.33-1.57 1.48-2.45 3.06-2.45ZM20.45 7.38c-.31 1.44-1.32 2.21-2.87 2.21s-2.23-.86-1.95-2.21c.29-1.34 1.3-2.21 2.88-2.21s2.23.77 1.93 2.21Z"
					className={pathClasses}
				/>
			</g>
		</svg>
	);
};

interface IStylableSvg {
	readonly pathClasses: string;
	readonly svgClasses?: string;
	readonly tooltip: string;
	readonly big?: boolean;
}
const EpicSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 84 : 42;
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 0 39.14 14.84" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<path d="M28.69 0H25.3l-.43 2.05h3.39L28.69 0z" className={pathClasses} />
				<path
					d="M33.64 4.92c1.29 0 1.9.44 1.98 1.21h3.51c.12-2.54-1.94-3.45-5.06-3.45s-5.67 1.16-6.7 3.51l.67-3.16h-3.39l-.62 2.94c-.15-2.04-1.7-3.16-4.19-3.16-1.37 0-2.68.5-3.59 1.37h-.03l.24-1.15h-3.29l-1.32 6.2H4.32l.36-1.94h6.72l.5-2.37H5.18l.36-1.91h7.63l.52-2.46H2.27L0 11.7h11.33l-.67 3.13h3.39l.83-3.92h.03c.44.64 1.49 1.13 2.99 1.13 2.57 0 4.56-1.33 5.5-3.16l-.6 2.81h3.39l.66-3.12c.02 2.36 1.9 3.47 5.21 3.47 3.73 0 5.71-1.36 6.76-3.75h-3.58C34.76 9.3 33.83 9.8 32.6 9.8c-1.63 0-2.37-.88-2.04-2.45.33-1.57 1.48-2.45 3.06-2.45ZM20.45 7.38c-.31 1.44-1.32 2.21-2.87 2.21s-2.23-.86-1.95-2.21c.29-1.34 1.3-2.21 2.88-2.21s2.23.77 1.93 2.21Z"
					className={pathClasses}
				/>
			</g>
		</svg>
	);
};
const EpicShareSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 150 : 89;
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 0 64.5 11.67" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<g id="Group_30" data-name="Group 30">
					<path
						id="Path_30"
						d="M42.23 2.75c-.34 0-.68.08-.99.23-.3.14-.56.35-.77.6h-.03V.1c.01-.06-.04-.1-.09-.1h-2.2a.1.1 0 0 0-.1.1v9.23c0 .05.04.1.09.1h2.2a.1.1 0 0 0 .1-.1V5.59c0-.14.02-.27.06-.4a.8.8 0 0 1 .77-.52c.6 0 .85.46.85 1.24v3.43c0 .05.04.1.1.1h2.2a.1.1 0 0 0 .1-.1V5.66c0-1.85-.82-2.9-2.29-2.9"
						className={pathClasses}
						data-name="Path 30"
					/>
					<path
						id="Path_31"
						d="M51.71 5.63c0-1.63-.77-2.88-3.02-2.88-.91-.02-1.81.17-2.63.55a.1.1 0 0 0-.05.11l.37 1.32c.01.05.07.08.12.07h.02a4.02 4.02 0 0 1 1.73-.43c.87 0 1.07.29 1.07.56v.06c-2.22 0-3.8.78-3.8 2.54 0 1.13.91 2.05 2.04 2.05h.12c.68.04 1.34-.22 1.8-.72h.05l.11.5s.05.08.1.08h1.95c.05 0 .1-.04.1-.09v-.02c-.06-.49-.09-.98-.09-1.48V5.64ZM49.4 6.98c0 .11 0 .22-.03.33-.12.35-.45.59-.82.6a.598.598 0 0 1-.67-.52v-.11c0-.55.51-.82 1.42-.84.06 0 .1.04.1.1v.44Z"
						className={pathClasses}
						data-name="Path 31"
					/>
					<path
						id="Path_32"
						d="M55.31 4.07h-.05l-.08-1.09c0-.05-.05-.09-.1-.09h-1.85a.1.1 0 0 0-.1.1c.03.52.05 1.13.05 2.11v4.23c0 .05.04.1.1.1h2.2a.1.1 0 0 0 .1-.1V6.21c0-.13 0-.27.03-.4.13-.53.6-.82 1.31-.82.17 0 .33.01.5.04.05 0 .1-.03.11-.08V2.79c-.16-.03-.32-.04-.48-.04-.8.02-1.5.55-1.74 1.32"
						className={pathClasses}
						data-name="Path 32"
					/>
					<path
						id="Path_33"
						d="M61.49 2.75c-2.4 0-3.38 1.96-3.38 3.48 0 2.07 1.3 3.33 3.6 3.33.81.02 1.61-.11 2.37-.38.05-.02.07-.06.06-.11l-.27-1.41c0-.05-.06-.09-.11-.08-.55.15-1.12.23-1.69.22-.76 0-1.45-.24-1.63-.67-.02-.05 0-.11.05-.13h3.86s.09-.03.1-.08c.05-.27.07-.55.07-.83 0-1.79-.91-3.34-3.01-3.34m.7 2.63h-1.73a.1.1 0 0 1-.1-.1s0-.01 0-.02c.05-.52.47-.93 1-.96.51 0 .93.43.92.94v.02c0 .05-.04.1-.09.11"
						className={pathClasses}
						data-name="Path 33"
					/>
					<path
						id="Path_34"
						d="M33.3 3.87h-.37l.02.56c0 .12-.2.2-.34.11l-1.73-1.11c-.06-.02-.08-.09-.06-.14a.09.09 0 0 1 .06-.06l1.65-1.22c.12-.09.34-.04.34.09l.02.56c.23-.01.46-.01.69 0 .85.09 1.63.16 1.95.21.09.02.17-.03.2-.12l.52-1.54a.18.18 0 0 0-.1-.22c-7.32-2.91-8.93 4.27-3.76 4.68l.2.02.6.06.02-.54c0-.12.22-.18.34-.09l1.65 1.22c.06.02.08.09.06.14a.09.09 0 0 1-.06.06l-1.73 1.11c-.13.08-.34.01-.34-.11l.02-.61-.73.03h-.2c-.73 0-1.46-.1-2.16-.31a.17.17 0 0 0-.2.1c-.09.2-.28.67-.68 1.76-.03.09 0 .18.09.22 2.28 1.02 7.15 1.34 7.6-1.84.39-3.09-2.42-2.95-3.56-3.02"
						className={pathClasses}
						data-name="Path 34"
					/>
					<path
						id="Path_35"
						d="M20.71.96h-2.45l-.31 1.48h2.45l.31-1.48Z"
						className={pathClasses}
						data-name="Path 35"
					/>
					<path
						id="Path_36"
						d="M24.29 4.52c.93 0 1.37.32 1.43.87h2.54c.08-1.83-1.4-2.49-3.66-2.49s-4.09.84-4.83 2.54l.48-2.28H17.8l-.45 2.12C17.25 3.8 16.13 3 14.33 3c-.96-.01-1.89.35-2.59 1h-.02l.18-.83H9.52l-.95 4.46H3.12l.26-1.4h4.85l.36-1.71H3.75l.26-1.38h5.51l.38-1.78H1.64L0 9.41h8.18l-.48 2.26h2.45l.6-2.83h.02c.32.46 1.07.82 2.16.82 1.65.05 3.19-.83 3.97-2.28l-.43 2.03h2.45l.48-2.25c0 1.7 1.37 2.51 3.76 2.51 2.7 0 4.12-.98 4.88-2.71h-2.59c-.35.72-1.11 1.15-1.9 1.09-1.18 0-1.71-.64-1.47-1.77a2.12 2.12 0 0 1 2.21-1.77m-9.52 1.78c-.16.99-1.07 1.69-2.07 1.6-1.12 0-1.6-.62-1.4-1.6.18-.99 1.08-1.68 2.08-1.6 1.14 0 1.61.56 1.39 1.6"
						className={pathClasses}
						data-name="Path 36"
					/>
				</g>
			</g>
		</svg>
	);
};
const EpicResearchSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 170 : 98;
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 0 80.54 13.21" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<path
					d="m2.55 7.9-.56.1a.276.276 0 0 1-.3-.18l-.24-.78c-.04-.12.03-.26.14-.32l.52-.23c.12-.05.2-.2.19-.32l-.02-.54c0-.13-.09-.27-.21-.31l-.53-.19c-.12-.04-.19-.18-.17-.3l.18-.79a.28.28 0 0 1 .28-.2l.56.06c.13.01.27-.07.33-.18l.26-.48c.07-.11.05-.28-.03-.38l-.36-.43a.285.285 0 0 1 0-.35l.55-.59c.09-.09.24-.11.35-.03l.46.33c.1.08.27.08.38 0l.46-.29c.11-.06.18-.21.16-.34L4.85.6c-.02-.13.06-.26.18-.3l.78-.24c.12-.04.26.03.32.14l.23.52c.05.12.2.2.32.19l.54-.02c.13 0 .27-.09.31-.21l.19-.53c.04-.12.18-.19.3-.17l.79.18c.12.03.21.16.2.28L8.95 1l-.64 1.37c-1.29-.47-2.8-.15-3.8.92S3.3 5.88 3.85 7.14l-1.32.73ZM10.03 1.95l-.76.98c.87.68 1.37 1.7 1.37 2.8 0 1.95-1.59 3.54-3.54 3.54-.88 0-1.73-.33-2.38-.92l-.26-.26-.01-.01-.06-.06-1.5 1.49-.07-.07a.145.145 0 0 0-.21 0L.04 11.98c-.06.06-.06.15 0 .21l.97.98c.06.06.15.06.21 0l2.56-2.54c.06-.06.06-.15 0-.21l-.07-.07.68-.67c.79.54 1.73.84 2.71.84 2.64 0 4.79-2.15 4.79-4.79 0-1.49-.68-2.87-1.86-3.79Z"
					className={pathClasses}
				/>
				<path
					d="M8.46 6.55H6.48l.1-.51h1.76l.14-.62H6.71l.1-.5h2l.14-.64h-3l-.6 2.92h2.97l.14-.65zM30.41 2.46h-1.97l-.25 1.19h1.97l.25-1.19z"
					className={pathClasses}
				/>
				<path
					d="M33.29 5.32c.75 0 1.1.25 1.15.7h2.04c.07-1.48-1.13-2-2.94-2s-3.29.68-3.89 2.04l.39-1.84h-1.97l-.36 1.71c-.09-1.18-.99-1.84-2.44-1.84-.8 0-1.56.29-2.08.8h-.02l.14-.67H21.4l-.77 3.6h-4.38l.21-1.13h3.91l.29-1.38h-3.91l.21-1.11h4.43l.3-1.43h-6.64l-1.32 6.47h6.59l-.39 1.82h1.97l.48-2.28h.02c.26.37.86.66 1.74.66 1.49 0 2.65-.77 3.2-1.84l-.35 1.63h1.97l.39-1.81c0 1.37 1.1 2.02 3.03 2.02 2.17 0 3.32-.79 3.93-2.18h-2.08c-.28.59-.82.88-1.53.88-.95 0-1.38-.51-1.18-1.42.19-.91.86-1.42 1.78-1.42Zm-7.67 1.43c-.18.84-.76 1.28-1.67 1.28s-1.3-.5-1.13-1.28c.17-.78.76-1.28 1.68-1.28s1.3.45 1.12 1.28ZM43.95 7.31c-.03-.1-.09-.23-.2-.41-.1-.18-.19-.29-.25-.35-.09-.08-.25-.16-.48-.24.32-.07.57-.16.77-.26.3-.16.55-.37.76-.62.03-.05.07-.09.1-.14.17-.24.29-.51.35-.83.09-.42.06-.77-.08-1.07-.14-.29-.37-.49-.67-.6-.31-.11-.78-.16-1.41-.16h-3.39l-1.41 6.58h2.04l.57-2.67h.18c.19 0 .34.05.47.15.09.08.18.25.26.51l.66 2.01h2.3l-.2-.65c-.09-.13-.17-.27-.24-.43-.1-.25-.15-.52-.14-.82Zm-1.02-2.68c-.03.14-.1.27-.21.38a.73.73 0 0 1-.37.21c-.28.06-.47.09-.56.09h-.86l.29-1.34h.89c.37 0 .61.06.72.17s.14.28.1.49Z"
					className={pathClasses}
				/>
				<path
					d="M47.67 7.71c-.15.15-.29.26-.41.32-.19.1-.39.14-.58.14-.3 0-.53-.11-.67-.33-.1-.15-.14-.39-.1-.7h3.92l.05-.22c.05-.22.08-.43.1-.62a1.13 1.13 0 0 1-.05-.71c0-.03.02-.06.03-.09-.01-.07-.03-.15-.05-.21-.13-.42-.38-.74-.76-.96-.37-.22-.93-.33-1.66-.33-.9 0-1.66.25-2.27.74-.21.17-.39.35-.55.55-.03.04-.05.08-.08.12 0 0-.01.02-.02.02-.25.36-.43.77-.53 1.24-.05.23-.07.44-.07.64l.38 1.26c.14.2.32.36.53.47.35.19.86.29 1.53.29.78 0 1.4-.11 1.86-.33.36-.17.7-.44 1.03-.79-.03-.11-.04-.22-.05-.35l-1.56-.15Zm-1.18-2.19c.24-.25.51-.37.82-.37.27 0 .47.08.6.24.13.16.17.43.13.81h-1.92c.1-.3.22-.53.37-.69ZM44.1 8.13c.06.16.14.31.24.43l-.38-1.26c0 .3.04.57.14.82ZM44.58 5.41s.06-.08.08-.12c-.04.05-.07.09-.1.14l.02-.02Z"
					className={pathClasses}
				/>
				<path
					d="M54.58 6.7c-.12-.21-.31-.38-.57-.5-.29-.13-.75-.23-1.38-.31-.42-.05-.68-.11-.8-.17-.11-.06-.16-.16-.13-.28a.45.45 0 0 1 .21-.29c.12-.08.29-.12.51-.12s.39.05.52.15c.1.08.15.2.16.36l1.89-.18c-.06-.33-.16-.6-.31-.79s-.36-.34-.63-.43c-.27-.09-.71-.14-1.32-.14-.58 0-1.05.06-1.42.18-.37.12-.68.3-.92.54-.23.22-.37.48-.45.78.04.23.05.5.03.8.01.04.03.07.05.11.13.24.32.41.56.51.24.1.69.21 1.34.33.44.08.7.15.8.2.13.08.18.19.15.33-.03.12-.1.23-.23.31-.17.11-.38.17-.65.17-.24 0-.42-.05-.54-.15-.12-.1-.2-.26-.23-.49l-1.98.18v.07l.32.03c-.09.11-.18.22-.27.32.07.29.23.54.46.72.34.27.97.4 1.9.4.66 0 1.2-.08 1.61-.25.42-.16.74-.39.98-.66.14-.17.25-.34.33-.52-.09-.35-.09-.75 0-1.2Z"
					className={pathClasses}
				/>
				<path
					d="M49.24 7.86c0 .12.02.24.05.35.09-.1.18-.2.27-.32l-.32-.03ZM49.97 6.31c.03-.3.02-.57-.03-.8 0 .03-.02.06-.03.09-.06.26-.04.5.05.71ZM58.23 7.71c-.15.15-.29.26-.41.32-.19.1-.39.14-.58.14-.3 0-.53-.11-.67-.33-.1-.15-.14-.39-.1-.7h3.81c.04-.05.09-.11.14-.15v-.07c.16-.67.16-1.22.03-1.64-.13-.42-.38-.74-.76-.96-.37-.22-.93-.33-1.66-.33-.9 0-1.66.25-2.27.74-.62.49-1.01 1.14-1.18 1.93v.03l.03.06c.12.24.14.51.08.8-.02.11-.07.23-.12.34.02.08.04.16.08.24.17.41.42.72.77.91.35.19.86.29 1.53.29.78 0 1.4-.11 1.86-.33.39-.19.76-.48 1.11-.87 0-.08.01-.16.03-.24l-1.72-.16Zm-1.18-2.19c.24-.25.51-.37.82-.37.27 0 .47.08.6.24.13.16.17.43.13.81h-1.92c.1-.3.22-.53.37-.69Z"
					className={pathClasses}
				/>
				<path
					d="m54.62 6.76-.03-.06c-.09.45-.09.84 0 1.2.05-.11.09-.22.12-.34.06-.29.04-.56-.08-.8ZM65.45 8.69c0-.14.02-.32.08-.56l.48-2.25c.05-.24.06-.49.02-.76-.04-.26-.13-.47-.26-.61-.19-.21-.46-.34-.8-.41-.34-.07-.8-.1-1.39-.1-.36 0-.71.03-1.03.08-.33.05-.59.13-.79.23-.28.14-.5.3-.68.49-.17.19-.33.44-.47.77l1.82.2c.12-.22.26-.37.4-.45.18-.1.43-.15.75-.15.25 0 .41.05.49.14.08.1.09.26.04.5-.26.1-.51.17-.74.23-.23.06-.73.16-1.49.31-.64.12-1.1.31-1.38.56-.03.03-.05.06-.08.09l-.03.15h-.1c-.16.2-.27.44-.33.71v.01l.16.02c-.06.08-.13.16-.19.23-.01.3.08.56.28.78.25.28.69.42 1.29.42.46 0 .87-.07 1.25-.21.28-.11.57-.28.88-.53v.32c0 .07.03.17.08.3h1.78l.03-.12c-.04-.16-.06-.29-.06-.4ZM63.83 7.1c-.05.24-.13.44-.24.59-.11.15-.27.28-.48.39-.21.11-.42.16-.64.16s-.36-.05-.44-.15c-.09-.1-.11-.22-.08-.38.03-.13.11-.25.24-.36.13-.1.4-.21.82-.31.31-.07.6-.16.89-.25l-.07.31Z"
					className={pathClasses}
				/>
				<path
					d="M59.96 7.88c-.02.08-.03.17-.03.24.06-.07.13-.15.19-.23l-.16-.02ZM60.39 7.15l.03-.15s-.1.1-.14.15h.1ZM70.09 4.98l.46-.71c-.3-.18-.6-.27-.89-.27s-.52.07-.74.2c-.22.14-.45.38-.7.75l.18-.83h-1.83L65.5 9.09l.03.12h1.91l.37-1.71c.17-.82.39-1.37.66-1.67.19-.21.41-.32.66-.32.12 0 .27.04.45.12.05-.09.1-.18.16-.26.1-.14.22-.27.35-.4Z"
					className={pathClasses}
				/>
				<path
					d="M65.48 9.21h.06l-.03-.12-.03.12ZM73.12 7.22c-.14.27-.32.47-.52.6s-.43.2-.69.2c-.31 0-.54-.11-.69-.33-.15-.22-.18-.53-.09-.95.1-.47.28-.82.53-1.05.25-.23.54-.35.86-.35.26 0 .45.06.58.17.13.11.2.27.21.49l1.82-.24.04-.19c-.08-.48-.28-.85-.61-1.12-.37-.3-.98-.45-1.81-.45-.59 0-1.1.08-1.51.23-.26.1-.53.25-.8.45-.12.09-.23.19-.34.29l-.44.68s-.05-.02-.07-.03c-.17.3-.3.65-.39 1.05-.11.5-.12.9-.04 1.21a1.788 1.788 0 0 0 1.26 1.28c.34.1.78.15 1.32.15s1.04-.08 1.43-.24c.4-.16.74-.38 1.04-.66.17-.16.32-.35.46-.55l.1-.47-1.65-.19Z"
					className={pathClasses}
				/>
				<path
					d="M70.09 4.98c-.13.13-.25.26-.35.4-.06.08-.11.17-.16.26.02 0 .04.02.07.03l.44-.68ZM80.32 4.49c-.24-.32-.62-.48-1.15-.48-.36 0-.68.06-.97.18-.29.12-.59.32-.92.59l.56-2.59h-1.95l-.73 3.39c0 .06.02.12.02.19h-.07l-.35 1.66.16.02c-.08.16-.17.31-.26.45l-.29 1.33h1.95l.52-2.45c.1-.46.24-.79.43-.96.19-.18.4-.27.63-.27.21 0 .37.07.46.2.09.13.1.36.03.68l-.6 2.8h1.96l.69-3.24c.14-.67.1-1.16-.14-1.49Z"
					className={pathClasses}
				/>
				<path
					d="m74.77 7.41-.1.47c.1-.14.19-.29.26-.45l-.16-.02ZM75.18 5.75c0-.06-.01-.13-.02-.19l-.04.19h.07Z"
					className={pathClasses}
				/>
			</g>
		</svg>
	);
};
const MyChartSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 100 : 61;
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 0 41.74 10.98" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<g id="Group_49" data-name="Group 49">
					<path
						id="Path_72"
						d="M18.47 8.38c.78 0 1.55-.22 2.22-.64.66-.38 1.18-.95 1.5-1.64l.14-.32h-1.31l-.07.11c-.25.41-.61.74-1.03.96-.44.25-.94.38-1.45.38-.81.02-1.59-.3-2.15-.88-.58-.57-.9-1.35-.88-2.16-.02-.81.3-1.6.88-2.17.56-.58 1.34-.91 2.15-.89.49 0 .97.13 1.4.37.42.21.77.53 1.01.93l.07.11h1.32l-.14-.32c-.32-.67-.83-1.24-1.47-1.61a4.26 4.26 0 0 0-5.2.59 4.04 4.04 0 0 0-1.24 2.98 4.04 4.04 0 0 0 1.24 2.98c.8.8 1.89 1.23 3.02 1.21"
						className={pathClasses}
						data-name="Path 72"
					/>
					<path
						id="Path_73"
						d="M23.79 5c-.03-.51.11-1.02.4-1.44.62-.62 1.62-.63 2.25-.01.3.39.44.87.41 1.36v3.33h1.18v-3.3a3.12 3.12 0 0 0-.72-2.17c-.52-.54-1.25-.84-2-.8-.56-.02-1.11.16-1.54.52V.13h-1.18v8.12h1.18V4.99Z"
						className={pathClasses}
						data-name="Path 73"
					/>
					<path
						id="Path_74"
						d="M31.64 8.39c.75.02 1.49-.24 2.06-.74v.6h1.18V2.13H33.7v.6c-.57-.49-1.3-.76-2.06-.74a3.11 3.11 0 0 0-2.28.93 3.217 3.217 0 0 0 0 4.54c.6.61 1.42.94 2.28.93m-2.03-3.18a2.058 2.058 0 0 1 3.51-1.51h.01c.4.41.61.95.6 1.51.03 1.14-.87 2.08-2.01 2.11-.56.01-1.11-.2-1.5-.6a2.1 2.1 0 0 1-.6-1.5"
						className={pathClasses}
						data-name="Path 74"
					/>
					<path
						id="Path_75"
						d="M40.38 2.13V.15H39.2v1.98h-1.03v.02h-.03c-.5 0-.99.17-1.38.49v-.51h-1.18v6.11c-.8.24-1.69.45-2.67.65-1.63.32-3.27.51-4.93.58-1.67.07-3.33.06-5-.03a58 58 0 0 1-4.97-.49c-.66-.09-1.28-.2-1.96-.3l-.52-.09c-.89-.14-1.72-.26-2.52-.36-.49-.06-1-.11-1.48-.16.26-.58.43-1.04.48-1.18l1.92-4.72h-1.29l-1.35 3.45-1.36-3.45H8.64l2.01 4.94v.03l-.08.19-.04.11c-.09.21-.17.42-.23.55h-.57c-.17 0-.34.02-.51.03-.18.02-.35.04-.52.07-.11.02-.21.04-.31.06V.26h-.47L4.2 4.94.46.26H0v8h1.31V3.27c.05.06.1.13.14.18L4 6.71h.37l2.58-3.25c.05-.06.1-.12.14-.19v4.99h.81c-.07.02-.14.05-.2.07-.37.12-.71.34-.96.64-.13.18-.22.38-.25.6l-.02.16v.09s0 .07.01.11c.02.12.06.23.11.34.11.2.27.37.47.49.18.11.38.18.59.21.13.02.27.03.41.03h.17c.09 0 .19-.01.28-.02.1-.01.19-.03.29-.05.37-.09.73-.25 1.05-.47.53-.33.95-.8 1.24-1.36l.17.02c.53.05 1.07.11 1.6.17.79.09 1.59.21 2.47.34l.57.09c.63.1 1.28.2 1.93.29 1.72.22 3.44.37 5.09.44 1.71.07 3.43.05 5.14-.06 1.7-.11 3.39-.36 5.06-.74 1.68-.36 3.32-.93 4.86-1.68l.13-.06v-.56l-.33.14c-.3.14-.64.28-1 .41V5.17c-.03-.48.11-.96.4-1.35.26-.29.62-.48 1.01-.54h.16l.88-.01v5.02h1.18V3.24h1.34V2.13H40.4ZM8.95 8.82c.1 0 .2 0 .28.01.16.01.31.02.47.05.05 0 .1.01.16.02-.18.28-.4.53-.64.75-.19.19-.43.33-.68.43-.06.02-.13.04-.18.06-.07.02-.14.03-.21.04-.13.02-.27.03-.4.02-.22 0-.42-.1-.55-.28a.502.502 0 0 1-.05-.13v-.13c0-.11.05-.21.11-.3.17-.2.39-.34.64-.4.29-.08.59-.12.89-.13h.17"
						className={pathClasses}
						data-name="Path 75"
					/>
				</g>
			</g>
		</svg>
	);
};
const OpenDotEpic: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 61 : 38;
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			id="Layer_2"
			data-name="Layer 2"
			viewBox="0 -5 29.76 28.17"
			width={width}
			className={svgClasses}
		>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<path
					d="M11.83 6.77c.2 0 .39-.03.57-.08.18-.06.34-.15.48-.28.14-.13.25-.31.33-.55.08-.23.12-.52.12-.88 0-.3-.04-.55-.12-.76a1.27 1.27 0 0 0-.32-.5c-.13-.12-.28-.21-.46-.26-.17-.05-.35-.08-.54-.08-.28 0-.51.03-.69.07V6.7c.07.02.17.03.32.05.15.01.25.02.31.02Z"
					className={pathClasses}
				/>
				<path
					d="M29.75 1.73c0-.96-.77-1.73-1.73-1.73H1.73C.77 0 0 .78 0 1.74v14.7c0 .96.78 1.73 1.73 1.73h26.29c.96 0 1.73-.78 1.73-1.74V1.73ZM7.66 10.46c-.56.85-1.4 1.27-2.52 1.28-.6 0-1.11-.11-1.53-.34s-.77-.56-1.05-.99c-.27-.43-.48-.95-.61-1.56-.13-.61-.2-1.29-.2-2.05 0-1.62.26-2.85.81-3.69.55-.85 1.39-1.27 2.53-1.28.59 0 1.1.11 1.53.34.43.23.78.56 1.05.99.27.43.48.95.61 1.56.13.61.2 1.29.21 2.05 0 1.62-.27 2.85-.83 3.69Zm1.82-8.37c.33-.07.68-.13 1.07-.18.39-.04.78-.06 1.17-.07.42 0 .83.03 1.23.11s.77.23 1.08.45c.32.23.58.54.78.96.2.41.3.95.31 1.61 0 .6-.08 1.11-.25 1.53-.17.42-.39.76-.67 1.02s-.6.46-.97.58c-.37.12-.75.18-1.14.18h-.44c-.09 0-.18-.01-.27-.02-.09 0-.15-.02-.18-.03v3.28H9.5l-.04-9.44Zm8.44 13.41c.29 0 .5-.12.61-.35h.83c-.24.56-.7.87-1.57.87-.77 0-1.21-.26-1.21-.81l-.15.73h-.79l.14-.65c-.22.43-.68.74-1.28.74-.35 0-.59-.11-.7-.26l-.19.91h-.79l.15-.73h-2.64l.53-2.59h2.66l-.12.57h-1.78l-.08.45h1.57l-.12.55h-1.57l-.08.45h1.75l.31-1.45h.77l-.06.27c.21-.2.51-.32.83-.32.58 0 .94.26.98.74l.14-.68h.79l-.16.74c.24-.55.81-.82 1.56-.82.73 0 1.21.21 1.18.8h-.82c-.02-.18-.16-.28-.46-.28-.37 0-.64.21-.71.57-.08.36.1.57.48.57Zm-1.81-1.81.1-.48H17l-.1.48h-.79Zm-.09-2.2-.04-9.53 4.77-.02v1.58l-3.04.01v2.34l2.77-.01v1.58l-2.76.01V9.9l3.12-.01v1.58l-4.81.02Zm10.66.02-2.73-4.74-.63-1.67h-.07l.23 1.65.02 4.71h-1.62l-.04-9.59h1.32l2.76 4.77.59 1.64h.08l-.23-1.62-.02-4.75h1.62L28 11.5h-1.32Z"
					className={pathClasses}
				/>
				<path
					d="M14.63 14.43c-.37 0-.6.2-.67.51-.07.31.08.51.45.51.36 0 .6-.18.67-.51.07-.34-.09-.51-.45-.51ZM6.34 4.39c-.12-.3-.29-.54-.49-.72-.2-.18-.45-.26-.75-.26-1.04 0-1.56 1.13-1.55 3.38 0 .48.03.93.09 1.34.06.41.15.77.28 1.07.13.3.29.53.49.71.2.17.44.26.73.26.53 0 .92-.27 1.18-.81.26-.54.39-1.4.38-2.57 0-.46-.03-.9-.09-1.31-.06-.41-.15-.77-.27-1.07Z"
					className={pathClasses}
				/>
			</g>
		</svg>
	);
};

const Showroom: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 120 : 90;
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 306.03 49.67"
			width={width}
			className={svgClasses}
		>
			<title>{tooltip}</title>
			<defs>
				<style></style>
			</defs>
			<g
				style={{
					isolation: "isolate",
				}}
			>
				<g id="Layer_2" data-name="Layer 2">
					<g id="Layer_1-2" data-name="Layer 1">
						<path d="M31.97 39.64h.05-.06Z" className={pathClasses} strokeWidth={0} />
						<path
							d="M31.97 39.64c-5.17.01-9.83.58-13.22 1.48 3.38-.9 8.04-1.46 13.22-1.48ZM15.49 36.13l-2.16 8.06c-6.88-.42-12.06-2.04-12.06-3.99 0-2.15 6.26-3.91 14.22-4.07Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M50.86 43.94c.06.1.1.21.14.31 8.36-.27 14.83-2.16 14.83-4.45 0-2.47-7.58-4.48-16.92-4.48-.94 0-1.85.02-2.75.06l4.7 8.56ZM32.15 39.64c8.87 0 16.29 1.62 18.34 3.8-2.05-2.18-9.47-3.8-18.34-3.8h-.12.12Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M31.97 39.64h.06c.04-.26 9.72-12.23 9.72-12.23L27.73 2.13c-.73-1.33-2.71-1.1-3.1.37l-7.1 26.12s14.25 10.64 14.44 11.02ZM46.16 35.38c-5.08.22-9.4 1.04-11.89 2.18 2.49-1.14 6.8-1.96 11.89-2.18Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M31.97 39.64c-1.29.01-2.55.04-3.77.11-1.82.1-3.55.27-5.14.51-1.58.23-3.03.52-4.3.86h-.01a20.374 20.374 0 0 0-2.06.65c-.31.11-.6.23-.87.36-.27.12-.52.25-.75.38-.93.51-1.53 1.08-1.74 1.68l2.16-8.06 2.04-7.51s14.25 10.64 14.44 11.02Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M50.86 43.94c-.08-.15-.19-.3-.32-.45 0-.02-.04-.04-.05-.05-2.05-2.19-9.47-3.8-18.34-3.8h-.12c.04-.26 9.72-12.22 9.72-12.22l4.42 7.97 4.69 8.56Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M32.11 40.31c-.24 2.21-7.04 3.98-15.41 3.98-1.16 0-2.28-.04-3.37-.1 0 .03-.03.05-.04.08v.01c-.04.12-.07.24-.07.37 0 2.77 8.48 5.01 18.93 5.01s18.93-2.25 18.93-5.01c0-.13-.03-.27-.08-.4-.69.02-1.38.04-2.09.04-8.69 0-15.84-1.74-16.8-3.98Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M51 44.25c-.69.03-1.38.04-2.09.04-8.69 0-15.85-1.74-16.8-3.98-.24 2.2-7.04 3.98-15.41 3.98-1.16 0-2.28-.04-3.37-.11.41-1.2 2.41-2.27 5.43-3.07 3.38-.9 8.04-1.46 13.21-1.48h.18c8.87 0 16.29 1.62 18.34 3.8l.05.05c.13.14.24.29.32.45.05.11.1.21.14.31Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="M18.75 41.12c-3.02.8-5.01 1.88-5.42 3.07.41-1.2 2.41-2.27 5.42-3.07ZM50.49 43.44s.04.03.06.05c-.02-.02-.04-.03-.06-.05Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="m17.53 28.62-2.04 7.51c-7.96.16-14.22 1.92-14.22 4.07 0 0-1.27-21.27-1.27-21.32 0-.93.75-1.68 1.68-1.68.42 0 .8.15 1.09.42h.01l14.75 11Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="m66.22 1.34-.39 38.46c0-2.47-7.57-4.48-16.92-4.48-.94 0-1.85.02-2.75.06l-4.42-7.97S63.82.45 63.83.44c.25-.27.61-.44 1.01-.44.75 0 1.36.59 1.38 1.34Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path
							d="m75.77 39.83 4.26-2.46c1.12 1.83 2.58 3.17 5.15 3.17 2.17 0 3.55-1.08 3.55-2.58 0-1.79-1.42-2.43-3.81-3.47l-1.31-.56c-3.77-1.61-6.27-3.62-6.27-7.88 0-3.92 2.99-6.91 7.65-6.91 3.32 0 5.71 1.16 7.43 4.18l-4.07 2.61c-.9-1.61-1.87-2.24-3.36-2.24s-2.5.97-2.5 2.24c0 1.57.97 2.2 3.21 3.17l1.31.56c4.44 1.9 6.95 3.85 6.95 8.21 0 4.71-3.7 7.28-8.66 7.28s-7.99-2.31-9.52-5.34ZM99.89 19.44h5.23v12.99h10.87V19.44h5.23v25.39h-5.23V37.1h-10.87v7.73h-5.23V19.44ZM127.52 32.14c0-7.5 5.83-13.03 13.14-13.03s13.14 5.53 13.14 13.03-5.82 13.03-13.14 13.03-13.14-5.53-13.14-13.03Zm20.91 0c0-4.74-3.14-8.36-7.77-8.36s-7.77 3.62-7.77 8.36 3.14 8.36 7.77 8.36 7.77-3.62 7.77-8.36ZM155.61 19.44h5.53l3.7 10.19c.63 1.75 1.34 4.59 1.34 4.59s.75-2.88 1.57-4.59l4.89-10.42h.52l4.97 10.42c.82 1.72 1.57 4.59 1.57 4.59s.71-2.84 1.34-4.59l3.66-10.19h5.08l-9.22 25.61h-.52l-5.71-12.25c-.67-1.42-1.42-3.77-1.42-3.77s-.78 2.35-1.46 3.77l-5.94 12.25h-.52l-9.37-25.61ZM213.82 44.83h-6.05l-4.18-6.42c-.49.07-1.01.11-1.49.11h-1.79v6.31h-5.23V19.44h7.02c7.09 0 11.35 3.4 11.35 9.6 0 3.96-1.75 6.46-4.89 7.88l5.26 7.92Zm-11.87-10.87c4.26 0 6.12-1.34 6.12-4.93s-1.94-5-6.12-5h-1.64v9.93h1.64ZM218.01 32.14c0-7.5 5.83-13.03 13.14-13.03s13.14 5.53 13.14 13.03-5.82 13.03-13.14 13.03-13.14-5.53-13.14-13.03Zm20.91 0c0-4.74-3.14-8.36-7.77-8.36s-7.77 3.62-7.77 8.36 3.14 8.36 7.77 8.36 7.77-3.62 7.77-8.36ZM248.74 32.14c0-7.5 5.83-13.03 13.14-13.03s13.14 5.53 13.14 13.03-5.82 13.03-13.14 13.03-13.14-5.53-13.14-13.03Zm20.91 0c0-4.74-3.14-8.36-7.77-8.36s-7.77 3.62-7.77 8.36 3.14 8.36 7.77 8.36 7.77-3.62 7.77-8.36ZM280.34 19.18h.41l12.51 13.74 12.32-13.74h.45v25.65h-5.04v-9.52c0-1.72.19-4.44.19-4.44s-1.53 2.24-2.73 3.58l-5.04 5.64h-.49l-5.04-5.64c-1.2-1.34-2.73-3.58-2.73-3.58s.19 2.73.19 4.44v9.52h-5V19.18Z"
							className={pathClasses}
							strokeWidth={0}
						/>
						<path d="M129.24.75h-3.71l-.47 2.24h3.71l.47-2.24z" />
						<path
							d="M134.65 6.13c1.41 0 2.07.48 2.16 1.32h3.84c.13-2.78-2.13-3.77-5.54-3.77s-6.2 1.27-7.32 3.84l.73-3.46h-3.71l-.68 3.21c-.16-2.23-1.85-3.46-4.59-3.46-1.5 0-2.93.54-3.92 1.5h-.03l.27-1.26h-3.6l-1.44 6.78h-8.23l.39-2.13h7.35l.55-2.59h-7.35l.4-2.08h8.34l.57-2.69h-12.5l-2.49 12.18h12.39l-.73 3.43h3.71l.91-4.28h.03c.48.7 1.62 1.24 3.27 1.24 2.81 0 4.98-1.45 6.02-3.45l-.65 3.07h3.71l.72-3.41c.02 2.58 2.08 3.79 5.7 3.79 4.08 0 6.24-1.48 7.39-4.1h-3.92c-.52 1.11-1.55 1.65-2.88 1.65-1.78 0-2.59-.96-2.23-2.67.36-1.71 1.62-2.67 3.35-2.67Zm-14.43 2.69c-.33 1.57-1.44 2.41-3.13 2.41s-2.44-.94-2.13-2.41c.31-1.47 1.42-2.41 3.15-2.41s2.44.84 2.11 2.41Z"
							className={pathClasses}
							strokeWidth={0}
						/>
					</g>
				</g>
			</g>
		</svg>
	);
};

const GetConnected: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 180 : 120;

	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 864 216" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<path
				className={pathClasses}
				d="M57.087 141.757c9.586 0 19.422-2.09 28.027-5.78V93.198H67.411v30.855c-2.457.985-5.777 1.473-9.344 1.473-12.66 0-29.378-5.652-29.378-28.52 0-20.035 14.996-28.273 30.363-28.273 8.73 0 17.457 2.336 23.973 6.762V57.793c-7.993-3.567-17.211-5.532-26.43-5.532-23.606 0-46.84 13.524-46.84 44.746 0 31.594 23.113 44.75 47.332 44.75ZM129.491 141.757c8.852 0 16.106-1.355 21.637-3.691v-13.398c-5.043 2.09-11.066 3.44-17.824 3.44-9.715 0-18.813-3.44-20.778-12.046h41.551c3.934-17.457-3.933-36.758-28.398-36.758-13.278 0-30.364 8.977-30.364 31.227 0 20.531 15.489 31.226 34.176 31.226ZM112.401 105c.246-8.114 6.641-12.665 13.895-12.665 10.57 0 12.906 8.606 12.168 12.665ZM191.693 141.757c5.902 0 11.308-.984 15.859-2.582v-13.77c-3.441 1.231-6.762 1.845-10.695 1.845-6.395 0-9.344-2.582-9.344-8.727V95.41h18.562V81.027h-18.562V59.76l-17.086 4.055v17.21h-9.957V95.41h9.957v26.43c0 14.628 9.832 19.917 21.266 19.917ZM288.317 141.757c9.098 0 17.703-1.968 25.078-5.535v-18.07c-5.285 4.55-13.398 7.133-22.25 7.133-14.875 0-30.367-8.36-30.367-27.293.125-20.653 15.367-29.258 30.367-29.258 7.988 0 15.98 2.336 21.637 6.762V57.793c-7.5-3.688-16.106-5.532-24.711-5.532-22.988 0-46.102 14.262-46.223 45.73 0 29.997 23.48 43.766 46.469 43.766ZM354.33 141.757c17.211 0 32.578-10.574 32.578-31.226 0-20.653-15.367-31.227-32.578-31.227-17.09 0-32.578 10.574-32.578 31.227 0 20.652 15.488 31.226 32.578 31.226Zm0-15.367c-9.836 0-15.12-6.762-15.12-15.86 0-9.097 5.284-15.859 15.12-15.859 9.832 0 15.121 6.762 15.121 15.86 0 9.097-5.289 15.86-15.12 15.86ZM398.093 140.035h17.086v-38.477c2.582-3.566 7.132-7.379 13.156-7.379 6.515 0 11.43 3.692 11.43 11.559v34.297h17.09v-36.512c0-13.645-8.485-24.219-23.973-24.219-8.48 0-15.121 3.938-19.668 9.223v-7.5h-15.121ZM470.621 140.035h17.086v-38.477c2.582-3.566 7.133-7.379 13.156-7.379 6.516 0 11.43 3.692 11.43 11.559v34.297h17.09v-36.512c0-13.645-8.484-24.219-23.973-24.219-8.48 0-15.12 3.938-19.668 9.223v-7.5h-15.12ZM574.005 141.757c8.852 0 16.106-1.355 21.637-3.691v-13.398c-5.043 2.09-11.067 3.44-17.824 3.44-9.715 0-18.813-3.44-20.778-12.046h41.551c3.934-17.457-3.933-36.758-28.398-36.758-13.278 0-30.364 8.977-30.364 31.227 0 20.531 15.489 31.226 34.176 31.226ZM556.915 105c.246-8.114 6.64-12.665 13.895-12.665 10.57 0 12.906 8.606 12.168 12.665ZM639.402 141.757c7.5 0 14.14-1.476 19.918-3.933v-15.121c-4.305 2.582-10.45 4.18-16.598 4.18-9.219 0-18.07-5.044-18.07-16.352 0-11.926 9.098-16.473 18.316-16.473 5.657 0 11.555 1.723 15.735 4.426v-15c-5.285-2.824-11.676-4.3-18.192-4.3-16.351 0-33.316 9.589-33.316 32.206 0 21.024 16.105 30.367 32.207 30.367ZM696.565 141.757c5.902 0 11.309-.984 15.86-2.582v-13.77c-3.442 1.231-6.762 1.845-10.696 1.845-6.394 0-9.344-2.582-9.344-8.727V95.41h18.563V81.027h-18.563V59.76L675.3 63.816v17.21h-9.957V95.41h9.957v26.43c0 14.628 9.833 19.917 21.266 19.917ZM753.114 141.757c8.851 0 16.105-1.355 21.637-3.691v-13.398c-5.043 2.09-11.067 3.44-17.825 3.44-9.715 0-18.812-3.44-20.777-12.046h41.55c3.934-17.457-3.933-36.758-28.398-36.758-13.277 0-30.363 8.977-30.363 31.227 0 20.531 15.488 31.226 34.176 31.226ZM736.024 105c.246-8.114 6.64-12.665 13.894-12.665 10.57 0 12.907 8.606 12.168 12.665ZM815.194 141.757c10.817 0 17.578-5.043 21.266-10.328v8.606h15.12V53.98h-17.085v33.195c-3.813-4.058-10.082-7.87-19.3-7.87-18.81 0-28.891 15.12-28.891 31.226 0 16.105 10.082 31.226 28.89 31.226Zm4.055-14.382c-9.711 0-15.488-7.625-15.488-16.844 0-9.219 5.777-16.84 15.488-16.84 7.375 0 12.91 4.793 15.246 9.832v14.016c-2.336 5.039-7.871 9.836-15.246 9.836Zm0 0"
			/>
		</svg>
	);
};
const ConnectionHub: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 180 : 120;

	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 864 216" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<path
				className={pathClasses}
				d="M50.092 150.027c8.437 0 16.418-1.824 23.257-5.133v-16.758c-4.902 4.219-12.425 6.614-20.636 6.614-13.793 0-28.156-7.754-28.156-25.31.113-19.151 14.25-27.132 28.156-27.132 7.41 0 14.82 2.168 20.066 6.27V72.164c-6.953-3.422-14.933-5.13-22.914-5.13-21.32 0-42.75 13.223-42.863 42.407 0 27.816 21.773 40.586 43.09 40.586ZM111.312 150.027c15.957 0 30.207-9.805 30.207-28.957s-14.25-28.957-30.207-28.957c-15.848 0-30.211 9.805-30.211 28.957s14.363 28.957 30.21 28.957Zm0-14.25c-9.121 0-14.024-6.274-14.024-14.707 0-8.438 4.903-14.707 14.024-14.707 9.117 0 14.02 6.27 14.02 14.707 0 8.433-4.903 14.707-14.02 14.707ZM151.895 148.43h15.844v-35.684c2.394-3.305 6.613-6.84 12.199-6.84 6.043 0 10.601 3.422 10.601 10.719v31.804h15.848v-33.86c0-12.651-7.867-22.456-22.23-22.456-7.868 0-14.024 3.648-18.243 8.55V93.71h-14.02ZM219.155 148.43h15.844v-35.684c2.394-3.305 6.613-6.84 12.199-6.84 6.043 0 10.601 3.422 10.601 10.719v31.804h15.848v-33.86c0-12.651-7.867-22.456-22.23-22.456-7.868 0-14.024 3.648-18.243 8.55V93.71h-14.02ZM315.028 150.027c8.207 0 14.934-1.254 20.063-3.422v-12.426c-4.672 1.938-10.258 3.192-16.528 3.192-9.008 0-17.441-3.192-19.265-11.172h38.53c3.65-16.188-3.648-34.086-26.335-34.086-12.313 0-28.156 8.324-28.156 28.957 0 19.039 14.363 28.957 31.691 28.957ZM299.18 115.94c.23-7.527 6.157-11.742 12.883-11.742 9.805 0 11.973 7.976 11.285 11.742ZM375.676 150.027c6.953 0 13.11-1.371 18.469-3.649v-14.023c-3.993 2.395-9.692 3.875-15.391 3.875-8.55 0-16.758-4.672-16.758-15.16 0-11.059 8.438-15.277 16.985-15.277 5.246 0 10.718 1.597 14.593 4.105v-13.91C388.672 93.367 382.746 92 376.704 92c-15.165 0-30.895 8.89-30.895 29.867 0 19.496 14.933 28.16 29.867 28.16ZM428.685 150.027c5.473 0 10.488-.914 14.707-2.395v-12.77c-3.191 1.141-6.27 1.712-9.918 1.712-5.93 0-8.664-2.395-8.664-8.094v-21.434h17.215V93.71H424.81V73.988l-15.848 3.762v15.96h-9.23v13.336h9.23v24.512c0 13.567 9.121 18.469 19.723 18.469ZM460.606 84.59c5.016 0 9.235-2.735 9.235-9.008 0-6.157-4.219-8.891-9.235-8.891-4.902 0-9.347 2.734-9.347 8.89 0 6.274 4.445 9.008 9.347 9.008Zm-7.98 63.84h15.848V93.71h-15.848ZM509.286 150.027c15.957 0 30.207-9.805 30.207-28.957s-14.25-28.957-30.207-28.957c-15.848 0-30.211 9.805-30.211 28.957s14.363 28.957 30.21 28.957Zm0-14.25c-9.122 0-14.024-6.274-14.024-14.707 0-8.438 4.902-14.707 14.024-14.707 9.117 0 14.02 6.27 14.02 14.707 0 8.433-4.903 14.707-14.02 14.707ZM549.869 148.43h15.843v-35.684c2.395-3.305 6.614-6.84 12.2-6.84 6.043 0 10.601 3.422 10.601 10.719v31.804h15.848v-33.86c0-12.651-7.867-22.456-22.23-22.456-7.868 0-14.024 3.648-18.243 8.55V93.71h-14.02ZM644.26 148.43h17.099v-32.15h35.796v32.15h17.102V68.627h-17.102v32.149H661.36V68.628H644.26ZM751.08 150.027c7.863 0 14.019-3.649 18.238-8.55v6.952h14.023V93.71h-15.847v35.68c-2.282 3.309-6.614 6.84-12.2 6.84-6.496 0-10.484-3.418-10.484-10.715V93.71h-15.848v33.856c0 12.656 7.41 22.46 22.117 22.46ZM830.652 150.027c17.324 0 26.676-14.024 26.676-28.957 0-14.934-9.352-28.957-26.676-28.957-8.664 0-14.48 3.535-18.016 7.297V68.628h-15.843v79.801h14.02v-7.98c3.534 4.902 9.804 9.578 19.839 9.578Zm-3.879-13.34c-6.84 0-11.969-4.445-14.137-9.121V114.57c2.168-4.672 7.297-9.117 14.137-9.117 9.121 0 14.363 7.066 14.363 15.617 0 8.55-5.242 15.617-14.363 15.617Zm0 0"
			/>
		</svg>
	);
};
const UserWebSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 100 : 61;
	return (
		<svg id="Layer_2" data-name="Layer 2" viewBox="0 0 55.41 14.04" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs></defs>
			<g id="Nav_Foot" data-name="Nav/Foot">
				<g id="Layer_2-2" data-name="Layer 2">
					<g id="Layer_1-2" data-name="Layer 1-2">
						<path d="M21.31 0h-1.28l-.16.77h1.28l.16-.77z" className={pathClasses} />
						<path
							d="M23.19 1.86c.49 0 .72.17.75.45h1.33c.04-.96-.74-1.3-1.92-1.3s-2.14.44-2.53 1.33l.25-1.19h-1.28l-.24 1.11c-.06-.77-.64-1.19-1.59-1.19-.52 0-1.01.19-1.36.52h-.01l.09-.44h-1.25l-.5 2.35h-2.85l.14-.74h2.54l.19-.89h-2.54l.14-.72h2.88l.2-.93h-4.32l-.86 4.21h4.29l-.25 1.19h1.28l.31-1.48h.01c.17.24.56.43 1.13.43.97 0 1.72-.5 2.08-1.19l-.23 1.06h1.28l.25-1.18c0 .89.72 1.31 1.97 1.31 1.41 0 2.16-.51 2.56-1.42h-1.36c-.18.38-.53.57-1 .57-.62 0-.9-.33-.77-.93s.56-.93 1.16-.93m-4.99.93c-.11.54-.5.83-1.08.83s-.84-.33-.74-.83.49-.83 1.09-.83c.59 0 .85.29.73.83Z"
							className={pathClasses}
						/>
						<path
							d="M36.42 11.22s.04-.06.04-.1c0-.06.02-.11.02-.17 0-.09.04-.19.04-.28 0-.05.01-.1.03-.15.02-.15.04-.3.07-.45.02-.06.02-.13.03-.2.03-.15.06-.31.08-.46.02-.13.05-.26.07-.39s.05-.25.07-.37l.03-.21.03-.21c.04-.17.06-.35.1-.53s.07-.37.11-.55l.09-.48c.03-.16.07-.32.1-.47s.06-.32.09-.48.07-.32.1-.47l.06-.36c.03-.16.07-.32.1-.49l.09-.46c.03-.16.07-.33.1-.49l.06-.33c0-.03.02-.05.05-.05h2.39s.05.02.04.06c-.01.08-.04.15-.06.22-.05.2-.1.4-.16.6l-.18.72c-.09.34-.16.68-.26 1.02s-.16.66-.26.99c-.07.22-.11.44-.17.66l-.18.7c-.09.34-.17.68-.26 1.02-.07.26-.14.52-.2.78-.07.27-.13.54-.21.81-.1.33-.16.66-.26.99-.07.22-.11.44-.17.66-.06.24-.12.47-.18.7-.07.27-.14.53-.2.8-.02.07-.03.08-.09.08h-2.54s-.04-.01-.05-.04c-.03-.14-.05-.27-.08-.41-.04-.17-.06-.35-.1-.53-.04-.16-.06-.32-.09-.48s-.07-.32-.1-.47c-.03-.17-.06-.34-.09-.5l-.06-.32c-.02-.11-.05-.22-.07-.33-.04-.22-.08-.44-.13-.66-.03-.11-.04-.23-.06-.35-.03-.16-.07-.32-.1-.49-.02-.12-.06-.25-.07-.37-.01-.11-.03-.21-.06-.32-.02-.07-.02-.15-.04-.23-.03-.14-.06-.29-.08-.44-.01-.08-.02-.15-.04-.23-.02-.15-.04-.3-.07-.45-.02-.06 0-.14-.03-.2 0-.1-.03-.2-.03-.3 0-.11-.02-.21-.04-.32v-.03c0-.11-.04-.22-.03-.34 0-.07-.04-.14-.03-.22-.03.02-.05.05-.05.09 0 .15-.04.28-.06.43-.01.13-.05.26-.07.4l-.03.21c-.02.14-.05.27-.07.41-.02.12-.04.24-.07.36-.03.17-.07.34-.1.51l-.09.46c-.04.17-.08.33-.11.5-.04.2-.09.39-.13.59l-.12.54-.12.58-.09.39c-.03.16-.07.32-.1.47l-.09.39c-.05.2-.09.4-.13.6l-.09.39c-.04.19-.09.39-.12.58 0 .05-.03.08-.09.08h-2.57s-.04-.02-.05-.04c-.03-.09-.06-.19-.08-.28-.03-.18-.09-.35-.12-.53-.04-.2-.1-.39-.14-.59-.03-.16-.08-.31-.12-.47-.04-.22-.11-.44-.15-.67-.02-.12-.06-.23-.08-.35-.05-.23-.12-.46-.16-.69-.03-.15-.08-.3-.11-.45-.05-.23-.12-.45-.16-.68-.02-.13-.07-.25-.1-.38-.04-.23-.11-.44-.15-.67-.02-.15-.07-.3-.11-.44-.05-.2-.1-.39-.14-.59-.03-.15-.08-.3-.11-.45-.05-.23-.11-.45-.16-.68-.03-.16-.08-.31-.11-.47-.04-.19-.1-.37-.13-.56-.03-.16-.08-.31-.11-.47-.05-.23-.12-.45-.16-.68-.02-.12-.06-.24-.09-.37a.85.85 0 0 1-.04-.22c0-.03.01-.04.04-.04h2.45c.11 0 .11 0 .14.12s.04.21.06.32c.03.18.07.36.1.54.03.13.05.26.06.39 0 .06.02.11.03.17.03.15.06.29.08.44.03.17.07.33.1.5l.06.34c.03.13.05.26.07.4.03.13.05.26.07.39 0 .06.02.11.03.17.03.14.05.27.08.41.03.12.05.25.07.37.03.13.05.26.07.39 0 .06.02.11.03.17.02.1.04.19.05.29 0 .06.02.12.03.18.02.08.03.17.05.25.03.14.05.28.07.42l.03.2c.03.16.05.32.08.48.03.14.03.28.06.41s.04.27.07.4c.02.09.03.18.03.27 0 .06.04.1.03.16 0 .01 0 .03.02.03.01 0 .03-.01.03-.03.02-.16.05-.32.08-.48.03-.13.04-.27.06-.4.02-.12.04-.24.07-.36.02-.07 0-.15.04-.21.04-.28.11-.55.16-.83l.12-.63c.03-.16.07-.32.1-.47.04-.2.07-.4.12-.59s.07-.39.12-.58c.04-.15.06-.3.09-.45.02-.1.03-.2.06-.3.04-.17.07-.34.11-.51l.09-.42c.02-.11.05-.23.07-.34l.09-.45c.02-.11.05-.23.08-.34l.09-.45c.01-.07.03-.15.05-.22 0-.03 0-.05.02-.08.01-.03.03-.05.06-.04h2.41c.13 0 .13 0 .15.13.03.17.07.34.1.51.02.12.05.25.07.37.01.1.03.2.06.3.04.13.05.26.08.38s.05.24.07.36.04.23.06.34c.03.18.06.36.1.54.03.11.04.23.06.34l.06.33c.02.12.05.25.07.37.04.18.06.36.11.54.03.11.04.23.06.35.02.13.05.25.07.38l.03.2c.01.07.03.14.04.21.03.17.06.34.08.51.02.13.06.26.07.4 0 .09.03.17.03.25 0 .05.03.09.03.15 0 .05 0 .1.02.15.02.07.02.14.03.21.03.16.03.32.06.48.02.11 0 .22.05.32l.02.02ZM2.46 6.39v3.14c0 .08 0 .15.02.23v.04c0 .14.02.29.05.43.03.22.09.43.17.64.07.2.18.39.3.57.16.22.37.39.62.49.18.09.37.14.57.14.07 0 .13.03.2.03.3-.02.59-.05.87-.17.11-.05.21-.11.3-.19.3-.22.48-.53.61-.88.07-.19.12-.39.16-.58.02-.09.03-.19.04-.28.02-.19 0-.38.03-.57.02-.16.02-.33.02-.49V3.1s.01-.03.03-.03H8.8s.04.01.04.04v6.21c0 .08 0 .16-.02.24v.08c0 .11.01.23-.03.34.01.11-.02.21-.03.32-.01.12-.03.23-.06.35-.05.21-.1.41-.16.61-.09.28-.21.56-.35.82-.14.25-.3.48-.5.69-.08.13-.21.21-.32.31-.14.12-.29.23-.45.32-.09.05-.17.1-.26.14-.29.15-.6.26-.92.34-.17.05-.35.08-.53.09-.44.08-.89.05-1.34.05-.06 0-.12-.03-.19-.03-.06.01-.13 0-.19-.02-.12 0-.24-.03-.36-.06-.22-.04-.44-.1-.65-.18-.14-.05-.27-.11-.4-.17-.14-.07-.28-.15-.41-.24-.1-.07-.2-.14-.28-.23-.04-.04-.1-.08-.14-.12-.2-.21-.39-.45-.54-.71-.15-.26-.28-.53-.37-.81-.08-.22-.14-.45-.18-.68-.02-.12-.05-.25-.07-.37v-.03c.01-.11-.02-.23-.04-.34-.02-.14 0-.29-.03-.43C0 9.41-.01 9.22 0 9.03V3.21c0-.19-.02-.16.16-.16h2.25s.05.01.05.05v3.27Zm47.3-1.66V6.9s0 .04.02.04.03 0 .05-.02c.04-.05.08-.11.13-.16l.24-.24c.11-.1.22-.18.35-.25a2.814 2.814 0 0 1 1.06-.36c.13-.02.26-.04.39-.05.13-.01.26-.01.4 0 .15.01.29.03.44.06.11.03.23.05.34.09.21.08.42.17.61.29.28.16.51.39.73.63.14.16.26.34.36.52.14.25.25.52.34.8.06.18.11.37.13.56.04.07.01.16.05.24v.09c0 .06 0 .12.02.17v.66c0 .18 0 .36-.03.54-.03.31-.1.62-.2.92-.07.23-.16.46-.27.67a4.121 4.121 0 0 1-.73.99c-.2.19-.42.36-.66.49-.23.13-.48.24-.73.31-.17.05-.34.08-.51.11-.26.03-.52.04-.77.02-.52-.04-1.02-.24-1.44-.56-.2-.16-.37-.35-.51-.56-.02-.03-.04-.07-.07-.1 0-.01-.02-.02-.03-.01 0 0-.02.02-.02.03-.02.13-.03.25-.03.38 0 .07-.03.13-.03.2s0 .14-.02.2c0 .06-.01.13 0 .19 0 .05-.02.07-.07.08h-1.95s-.05-.01-.05-.05c0-.12-.02-.24.02-.36v-.57c0-.07 0-.15.02-.21V2.69c0-.05-.02-.12.01-.16s.11-.02.17-.02h2.13c.13 0 .12 0 .12.12v2.11Zm0 5.22v.53c0 .13.04.25.03.38v.01c.05.22.14.42.26.61.05.08.11.16.19.23.09.08.18.15.28.21.21.14.45.18.69.2.09 0 .18 0 .27-.02.13-.01.26-.05.38-.09a.94.94 0 0 0 .35-.2c.15-.14.31-.28.4-.47.04-.07.06-.14.1-.21.05-.1.08-.2.11-.3l.06-.29c.02-.11.02-.22.05-.33 0-.24.03-.49-.03-.73 0-.12-.02-.24-.06-.36-.05-.25-.15-.48-.28-.7-.09-.15-.2-.28-.34-.38a1.513 1.513 0 0 0-.67-.29c-.2-.03-.4-.03-.59 0-.23.05-.44.16-.62.32-.2.17-.35.38-.44.63-.05.12-.08.24-.1.36-.02.17-.03.33-.03.5v.38Zm-5.45.78h-2.43c-.1 0-.11 0-.09.11.02.13.06.25.11.37.09.2.23.37.4.5.19.15.4.26.63.33.13.05.27.09.4.1.08.04.18 0 .26.05h.88c.07 0 .14-.03.21-.03.07 0 .14 0 .21-.02l.24-.03c.24-.04.47-.09.71-.15.07-.02.15-.04.22-.07.03-.01.07 0 .08.04v.01c.02.17.07.33.09.5.01.1.04.19.06.29.02.11.05.22.07.33.03.14.05.28.08.42 0 .02 0 .05-.03.06-.13.05-.27.1-.41.14-.16.05-.32.09-.48.12-.12.03-.25.05-.37.07h-.07c-.16.05-.33.07-.49.08-.1 0-.19.03-.28.03h-.28c-.17.03-.34.02-.52.02-.07 0-.14 0-.2-.02h-.09c-.15.01-.29-.02-.43-.03-.12-.01-.25-.03-.37-.06a4.27 4.27 0 0 1-.72-.21c-.21-.08-.4-.18-.59-.3a3.29 3.29 0 0 1-.73-.61c-.14-.15-.26-.31-.36-.49-.07-.12-.14-.24-.2-.37-.12-.26-.21-.53-.26-.8-.02-.12-.05-.25-.08-.37v-.01c0-.13-.04-.26-.03-.39v-.45c0-.2.01-.41.05-.61l.03-.2c.05-.27.13-.53.23-.79.06-.14.11-.28.18-.41.12-.23.26-.45.42-.66.15-.19.31-.36.49-.51.18-.16.38-.3.59-.41.25-.14.52-.25.8-.33.1-.03.21-.05.32-.07.08-.02.17-.03.25-.05h.03c.28-.03.56-.04.84-.02.18.01.36.03.53.07.17.04.34.08.51.14a2.957 2.957 0 0 1 1.32.92c.11.13.2.27.28.41.14.24.26.49.34.75.06.15.1.3.12.45.01.11.05.22.07.33v.01c0 .12.04.23.03.35 0 .05 0 .11.02.16v.53c0 .08-.03.15-.03.23a.6.6 0 0 1-.02.23v.09c0 .06-.03.12-.03.19 0 .02-.02.03-.04.03H44.3Zm-1.1-1.66h1.39s.05-.01.06-.06c0-.15-.02-.3-.06-.44-.04-.22-.13-.42-.26-.6-.1-.14-.23-.26-.38-.34-.22-.12-.47-.16-.72-.17-.05 0-.11.01-.16.03-.17 0-.33.06-.47.14-.26.15-.47.36-.6.63-.12.24-.2.5-.24.77 0 .02 0 .04.03.04h1.42Zm-23.72 1.66h-2.46s-.06.01-.06.06c0 .1.02.21.06.3.04.11.09.22.16.31.1.13.21.25.34.35.24.18.52.31.82.37.12.04.24.06.36.06.21.05.42.02.64.03h.28c.06 0 .12 0 .17-.02h.07c.14 0 .29-.02.43-.05.11-.02.23-.04.35-.06.15-.03.29-.07.43-.1.05-.01.11-.03.16-.04.1-.03.1-.03.12.07s.04.21.06.32c.03.17.07.33.1.5l.06.34c.02.07.03.15.04.22v.05c.01.11 0 .11-.1.15-.27.1-.54.18-.81.24-.12.03-.25.05-.37.07-.08.01-.16 0-.24.04h-.01c-.12 0-.23.04-.35.03-.09 0-.17.03-.26.03-.09-.01-.17 0-.26 0-.21.04-.42.01-.63.02-.08 0-.16-.03-.24-.03-.08 0-.16 0-.24-.02-.07-.02-.15-.02-.23-.03-.21-.03-.42-.08-.63-.14-.27-.07-.52-.17-.76-.3-.19-.1-.36-.22-.53-.35-.16-.14-.32-.28-.46-.44-.12-.14-.23-.29-.32-.45-.17-.29-.3-.6-.39-.92-.03-.11-.04-.22-.07-.33-.02-.09-.02-.18-.05-.26v-.06c0-.12-.04-.24-.03-.36-.04-.12-.02-.25-.02-.38 0-.06 0-.13.02-.19v-.05c0-.13 0-.26.03-.39.05-.36.15-.71.28-1.05.08-.2.18-.4.28-.58.08-.15.18-.3.3-.44.1-.13.2-.25.31-.36.21-.2.45-.38.7-.53.21-.13.44-.23.68-.3.15-.05.3-.1.45-.13.17-.03.34-.07.51-.06.21-.05.42-.03.63-.02.13 0 .26.02.39.04l.32.06c.17.04.35.09.51.16.19.08.38.17.55.29.12.07.23.16.33.26.13.12.26.26.37.4.09.13.18.25.25.38.14.24.25.5.33.77.05.18.1.35.13.54l.06.4c.02.13.02.28.02.42v.08c.02.07.02.15 0 .23v.14c0 .12 0 .24-.03.36v.09c0 .07-.02.14-.03.21 0 .02-.02.04-.05.04h-2.48Zm-1.11-1.66h1.41c.03 0 .05-.03.05-.06v-.09a.292.292 0 0 1-.02-.09c0-.08 0-.15-.03-.23a1.62 1.62 0 0 0-.25-.59c-.12-.18-.29-.33-.49-.42-.18-.07-.37-.12-.56-.12-.14 0-.28.01-.41.06-.21.05-.4.15-.55.3-.05.05-.1.1-.15.16-.09.1-.17.22-.23.35-.06.13-.11.27-.15.42-.02.08-.02.17-.05.25v.04c0 .01.01.03.03.03h1.41Z"
							className={pathClasses}
						/>
						<path
							d="M14.41 6.3c-.14.54-.27 1.07-.41 1.6 0 .02-.02.04-.03.06-.13-.06-.27-.11-.4-.17-.18-.08-.37-.13-.57-.18-.11-.03-.23-.04-.34-.06a2.72 2.72 0 0 0-.57-.02c-.16 0-.31.05-.45.13-.13.08-.24.18-.27.34-.04.23-.01.44.22.6.13.09.27.16.42.22.25.11.5.2.76.29.37.13.72.3 1.04.52.24.17.45.38.61.63.12.18.21.38.25.59l.06.36c.02.12.02.24.02.37 0 .28-.06.56-.17.83-.08.17-.17.33-.27.48-.03.04-.06.07-.09.1-.06.06-.12.11-.17.17-.15.15-.33.28-.52.38-.2.11-.42.2-.65.27-.14.04-.28.08-.42.1l-.39.06c-.1.01-.2.02-.3.02-.05 0-.1 0-.15.02h-.37c-.07 0-.14 0-.2-.02h-.06c-.16 0-.33 0-.49-.03-.13-.01-.27-.03-.4-.07-.1-.03-.2-.04-.3-.06-.23-.05-.45-.13-.67-.22-.08-.04-.17-.07-.25-.11-.06-.03-.06-.04-.04-.11.08-.31.16-.62.23-.93.06-.23.11-.45.17-.68 0-.03.03-.03.05-.03.04 0 .07.02.1.04.12.06.24.12.37.17.18.07.36.12.54.18.17.05.34.08.51.12.1.02.2.03.29.04h.44c.16 0 .31-.03.46-.09.14-.04.25-.13.32-.25.07-.13.1-.27.07-.42-.04-.23-.19-.36-.38-.47-.14-.08-.28-.14-.43-.19-.28-.1-.55-.19-.82-.3a2.73 2.73 0 0 1-.48-.25c-.17-.1-.33-.21-.48-.33-.12-.09-.22-.2-.32-.32-.11-.13-.21-.28-.28-.43-.07-.16-.12-.33-.16-.5-.03-.2-.04-.4-.02-.6.02-.22.07-.43.16-.64.08-.21.2-.41.34-.58.08-.09.16-.18.25-.26.17-.17.37-.31.58-.42.25-.13.52-.24.79-.3.13-.03.26-.05.38-.08s.25 0 .38-.04h.53c.06 0 .13 0 .19.02h.05c.13-.01.26.03.39.03.11.01.22.03.33.06.25.05.5.12.74.21.1.04.19.08.28.13Zm8.05 4.24V7.4c0-.06 0-.13-.02-.19v-.55c0-.08 0-.16-.02-.24a1.93 1.93 0 0 1 0-.34c0-.02.01-.03.03-.03h2.01s.06 0 .06.05v.1c0 .08 0 .16.02.24v.07c0 .15 0 .3.02.45.01.1.01.21 0 .31 0 .06.01.12.03.17 0 .01 0 .03.03.02.01 0 .02 0 .03-.02.02-.02.03-.05.04-.07.09-.24.22-.47.38-.67.19-.22.4-.43.67-.57.16-.08.33-.15.5-.21.06-.02.13 0 .18-.04.11 0 .22-.04.34-.03h.32c.06.01.12.03.18.04.02.06.02.12.01.17v2.02c0 .08-.02.1-.09.08-.06-.02-.12 0-.17-.02-.15-.04-.31-.02-.46-.02-.16 0-.31.02-.47.06-.54.13-.96.54-1.1 1.07-.03.13-.05.26-.06.4-.03.35-.02.7-.02 1.05v3.11c0 .06 0 .06-.06.06h-2.32s-.04-.01-.04-.04v-3.28Z"
							className={pathClasses}
						/>
					</g>
				</g>
			</g>
		</svg>
	);
};

const CosmosSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 150 : 70;

	return (
		<svg viewBox="50 120 1030 280" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs>
				<clipPath id="CosmosSvgb">
					<path className={pathClasses} d="M0 0h972.693v231.515H0z" data-name="Rectangle 73" />
				</clipPath>
				<clipPath id="CosmosSvgc">
					<path
						className={pathClasses}
						d="M10.03 52.827c19.039 20.686 45.2 1.641 35.82-20.168C36.937 11.938 16.114 21.493 6.38-.001-3.359 17.02-1.799 36.53 10.029 52.826"
						data-name="Path 66"
					/>
				</clipPath>
				<clipPath id="CosmosSvgd">
					<path
						className={pathClasses}
						d="M20.031 7.577c-27.76 18.976-24.708 48.56-2.844 64.786a74.228 74.228 0 0 1 16.1 15.82c48.8-10.981 58.618-33.342 46.9-64-5-10.505-19.42-24.182-37.379-24.184-7.157 0-14.873 2.169-22.781 7.576"
						data-name="Path 67"
					/>
				</clipPath>
				<clipPath id="CosmosSvge">
					<path
						className={pathClasses}
						d="M8.402 29.093c7.783 4.928 13.615 2.11 22.321-.709a61.08 61.08 0 0 1 23.114-3.323C49.037 11.92 39.807 1.299 22.805 0 .205 1.287-7.502 19.017 8.405 29.093"
						data-name="Path 68"
					/>
				</clipPath>
				<clipPath id="CosmosSvgf">
					<path
						className={pathClasses}
						d="M3.695.02C2.457.044 1.229.12 0 .184c23.221 1.777 31.948 20.924 34.694 40.155 4.094 28.656 17.3 41.951 35.364 36.7 23.326-6.772 21.378-38.07-2.983-57.887A107.313 107.313 0 0 0 5.781-.004q-1.041 0-2.085.02"
						data-name="Path 69"
					/>
				</clipPath>
				<clipPath id="CosmosSvgg">
					<path
						className={pathClasses}
						d="M75.33 68.428c-106.636 12.437-85.8 84.113-25.254 85.722.845.012 1.691.031 2.538.032h.247c.269 0 .538 0 .808-.008A107.836 107.836 0 0 0 148.991.001c13.61 35.61-1.777 60.043-73.661 68.428"
						data-name="Path 70"
					/>
				</clipPath>
				<clipPath id="CosmosSvgh">
					<path
						className={pathClasses}
						d="M37.253 14.17a107.835 107.835 0 0 0-4.22 159.2c-17.382-23.948-12.618-54.84 17.647-74.561 33.545-21.864 56.3-52.837 42.489-82.347C88.087 5.607 80.441.002 70.41.002c-9.123 0-20.219 4.635-33.157 14.17"
						data-name="Path 71"
					/>
				</clipPath>
				<clipPath id="CosmosSvga">
					<path d="M0 0h1153v500H0z" />
				</clipPath>
			</defs>
			<g clipPath="url(#CosmosSvga)" data-name="Group 105">
				<g data-name="Group 35">
					<g
						className={pathClasses}
						clipPath="url(#CosmosSvgb)"
						data-name="Group 34"
						transform="translate(89 151)"
					>
						<path
							d="M143.491 183.579c-8.018 3.872-25.712 8.295-48.383 8.295C35.941 191.874 0 154.826 0 98.149 0 36.771 42.854 0 99.807 0c22.4 0 38.154 4.424 45.065 8.017l-5.915 27.094c-8.847-3.87-22.56-7.188-37.767-7.188-37.877 0-65.249 23.777-65.249 68.566 0 40.918 23.778 66.907 64.972 66.907 13.548 0 27.923-2.765 37.048-6.635Z"
							data-name="Path 61"
						/>
						<path
							d="M225.603 192.149c-37.876 0-67.46-25.988-67.46-69.4 0-44.236 29.029-71.331 69.671-71.331 40.089 0 66.908 28.2 66.908 69.12 0 49.489-34.837 71.606-68.842 71.606Zm1.107-24.606c19.353 0 32.9-18.8 32.9-46.172 0-21.012-9.4-45.342-32.624-45.342-24.054 0-33.73 23.5-33.73 45.895 0 25.988 13 45.618 33.178 45.618Z"
							data-name="Path 62"
						/>
						<path
							d="M319.988 157.59c7.466 4.7 22.118 9.954 34.283 9.954 14.929 0 21.566-6.36 21.566-15.207 0-9.123-5.53-13.547-22.118-19.629-26.265-8.847-37.048-23.225-37.048-39.26 0-23.777 19.63-42.024 50.87-42.024 14.93 0 27.926 3.594 35.943 8.018l-6.913 23.777a59.486 59.486 0 0 0-28.475-7.741c-12.167 0-18.8 6.083-18.8 14.376 0 8.571 6.359 12.442 23.5 18.8 24.606 8.57 35.666 21.012 35.943 40.918 0 24.607-19.078 42.3-54.742 42.3-16.313 0-30.967-3.871-40.918-9.4Z"
							data-name="Path 63"
						/>
						<path
							d="M436.656 94.554c0-15.482-.553-28.477-1.105-40.089h29.03l1.659 19.906h.829c6.359-10.506 18.523-22.947 41.47-22.947 17.695 0 31.52 9.953 37.325 24.883h.552a53.07 53.07 0 0 1 16.313-16.865c7.741-5.253 16.312-8.018 27.648-8.018 22.118 0 44.788 15.206 44.788 58.336v79.353H601.99v-74.376c0-22.394-7.743-35.665-24.054-35.665-11.612 0-20.182 8.294-23.776 17.97a36.15 36.15 0 0 0-1.661 11.335v80.736h-33.176v-77.97c0-18.8-7.466-32.071-23.225-32.071-12.716 0-21.288 9.952-24.606 19.077a38.781 38.781 0 0 0-1.659 11.335v79.629h-33.177Z"
							data-name="Path 64"
						/>
						<path
							d="M883.943 157.59c7.466 4.7 22.118 9.954 34.284 9.954 14.928 0 21.566-6.36 21.566-15.207 0-9.123-5.531-13.547-22.118-19.629-26.266-8.847-37.049-23.225-37.049-39.26 0-23.777 19.631-42.024 50.87-42.024 14.93 0 27.927 3.594 35.944 8.018l-6.913 23.777a59.483 59.483 0 0 0-28.476-7.741c-12.166 0-18.8 6.083-18.8 14.376 0 8.571 6.361 12.442 23.5 18.8 24.607 8.57 35.667 21.012 35.944 40.918 0 24.607-19.078 42.3-54.743 42.3-16.311 0-30.967-3.871-40.917-9.4Z"
							data-name="Path 65"
						/>
					</g>
				</g>
				<g data-name="Group 37">
					<g
						clipPath="url(#CosmosSvgc)"
						data-name="Group 36"
						transform="translate(765.444 299.528)"
					>
						<path
							className={pathClasses}
							d="M-3.359 0h58.591v73.513H-3.359z"
							data-name="Rectangle 74"
						/>
					</g>
				</g>
				<g data-name="Group 39">
					<g clipPath="url(#CosmosSvgd)" data-name="Group 38" transform="translate(867.477 204.15)">
						<path
							className={pathClasses}
							d="M-7.727-.001h99.635v88.183H-7.727z"
							data-name="Rectangle 75"
						/>
					</g>
				</g>
				<g data-name="Group 41">
					<g clipPath="url(#CosmosSvge)" data-name="Group 40" transform="translate(821.732 167.01)">
						<path
							className={pathClasses}
							d="M-7.502 0h61.339v34.021H-7.502z"
							data-name="Rectangle 76"
						/>
					</g>
				</g>
				<g data-name="Group 43">
					<g
						clipPath="url(#CosmosSvgf)"
						data-name="Group 42"
						transform="translate(844.536 166.827)"
					>
						<path
							className={pathClasses}
							d="M-21.707 6.192 86.356-24.634 115.09 76.097 7.027 106.923z"
							data-name="Rectangle 77"
						/>
					</g>
				</g>
				<g data-name="Group 45">
					<g
						clipPath="url(#CosmosSvgg)"
						data-name="Group 44"
						transform="translate(798.675 228.333)"
					>
						<path
							className={pathClasses}
							d="M-31.306 0h193.907v154.181H-31.306z"
							data-name="Rectangle 78"
						/>
					</g>
				</g>
				<g data-name="Group 47">
					<g clipPath="url(#CosmosSvgh)" data-name="Group 46" transform="translate(742.44 178.987)">
						<path
							className={pathClasses}
							d="M-46.35 13.045 98.886-28.384l53.83 188.708L7.48 201.753z"
							data-name="Rectangle 79"
						/>
					</g>
				</g>
			</g>
		</svg>
	);
};

const CosmosMobileSvg: React.FC<IStylableSvg> = ({ pathClasses, svgClasses, tooltip, big }) => {
	const width = big ? 150 : 90;

	return (
		<svg viewBox="50 120 1030 280" width={width} className={svgClasses}>
			<title>{tooltip}</title>
			<defs>
				<clipPath id="CosmosMobileSvgb">
					<path className={pathClasses} d="M0 0h972.693v231.515H0z" data-name="Rectangle 73" />
				</clipPath>
				<clipPath id="CosmosMobileSvgc">
					<path
						className={pathClasses}
						d="M10.03 52.827c19.039 20.686 45.2 1.641 35.82-20.168C36.937 11.938 16.114 21.493 6.38-.001-3.359 17.02-1.799 36.53 10.029 52.826"
						data-name="Path 66"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvgd">
					<path
						className={pathClasses}
						d="M20.031 7.577c-27.76 18.976-24.708 48.56-2.844 64.786a74.228 74.228 0 0 1 16.1 15.82c48.8-10.981 58.618-33.342 46.9-64-5-10.505-19.42-24.182-37.379-24.184-7.157 0-14.873 2.169-22.781 7.576"
						data-name="Path 67"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvge">
					<path
						className={pathClasses}
						d="M8.402 29.093c7.783 4.928 13.615 2.11 22.321-.709a61.08 61.08 0 0 1 23.114-3.323C49.037 11.92 39.807 1.299 22.805 0 .205 1.287-7.502 19.017 8.405 29.093"
						data-name="Path 68"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvgf">
					<path
						className={pathClasses}
						d="M3.695.02C2.457.044 1.229.12 0 .184c23.221 1.777 31.948 20.924 34.694 40.155 4.094 28.656 17.3 41.951 35.364 36.7 23.326-6.772 21.378-38.07-2.983-57.887A107.313 107.313 0 0 0 5.781-.004q-1.041 0-2.085.02"
						data-name="Path 69"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvgg">
					<path
						className={pathClasses}
						d="M75.33 68.428c-106.636 12.437-85.8 84.113-25.254 85.722.845.012 1.691.031 2.538.032h.247c.269 0 .538 0 .808-.008A107.836 107.836 0 0 0 148.991.001c13.61 35.61-1.777 60.043-73.661 68.428"
						data-name="Path 70"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvgh">
					<path
						className={pathClasses}
						d="M37.253 14.17a107.835 107.835 0 0 0-4.22 159.2c-17.382-23.948-12.618-54.84 17.647-74.561 33.545-21.864 56.3-52.837 42.489-82.347C88.087 5.607 80.441.002 70.41.002c-9.123 0-20.219 4.635-33.157 14.17"
						data-name="Path 71"
					/>
				</clipPath>
				<clipPath id="CosmosMobileSvga">
					<path d="M0 0h1153v500H0z" />
				</clipPath>
			</defs>
			<g clipPath="url(#CosmosMobileSvga)" data-name="Group 105">
				<g data-name="Group 35">
					<g
						className={pathClasses}
						clipPath="url(#CosmosMobileSvgb)"
						data-name="Group 34"
						transform="translate(89 151)"
					>
						<path
							d="M143.491 183.579c-8.018 3.872-25.712 8.295-48.383 8.295C35.941 191.874 0 154.826 0 98.149 0 36.771 42.854 0 99.807 0c22.4 0 38.154 4.424 45.065 8.017l-5.915 27.094c-8.847-3.87-22.56-7.188-37.767-7.188-37.877 0-65.249 23.777-65.249 68.566 0 40.918 23.778 66.907 64.972 66.907 13.548 0 27.923-2.765 37.048-6.635Z"
							data-name="Path 61"
						/>
						<path
							d="M225.603 192.149c-37.876 0-67.46-25.988-67.46-69.4 0-44.236 29.029-71.331 69.671-71.331 40.089 0 66.908 28.2 66.908 69.12 0 49.489-34.837 71.606-68.842 71.606Zm1.107-24.606c19.353 0 32.9-18.8 32.9-46.172 0-21.012-9.4-45.342-32.624-45.342-24.054 0-33.73 23.5-33.73 45.895 0 25.988 13 45.618 33.178 45.618Z"
							data-name="Path 62"
						/>
						<path
							d="M319.988 157.59c7.466 4.7 22.118 9.954 34.283 9.954 14.929 0 21.566-6.36 21.566-15.207 0-9.123-5.53-13.547-22.118-19.629-26.265-8.847-37.048-23.225-37.048-39.26 0-23.777 19.63-42.024 50.87-42.024 14.93 0 27.926 3.594 35.943 8.018l-6.913 23.777a59.486 59.486 0 0 0-28.475-7.741c-12.167 0-18.8 6.083-18.8 14.376 0 8.571 6.359 12.442 23.5 18.8 24.606 8.57 35.666 21.012 35.943 40.918 0 24.607-19.078 42.3-54.742 42.3-16.313 0-30.967-3.871-40.918-9.4Z"
							data-name="Path 63"
						/>
						<path
							d="M436.656 94.554c0-15.482-.553-28.477-1.105-40.089h29.03l1.659 19.906h.829c6.359-10.506 18.523-22.947 41.47-22.947 17.695 0 31.52 9.953 37.325 24.883h.552a53.07 53.07 0 0 1 16.313-16.865c7.741-5.253 16.312-8.018 27.648-8.018 22.118 0 44.788 15.206 44.788 58.336v79.353H601.99v-74.376c0-22.394-7.743-35.665-24.054-35.665-11.612 0-20.182 8.294-23.776 17.97a36.15 36.15 0 0 0-1.661 11.335v80.736h-33.176v-77.97c0-18.8-7.466-32.071-23.225-32.071-12.716 0-21.288 9.952-24.606 19.077a38.781 38.781 0 0 0-1.659 11.335v79.629h-33.177Z"
							data-name="Path 64"
						/>
						<path
							d="M883.943 157.59c7.466 4.7 22.118 9.954 34.284 9.954 14.928 0 21.566-6.36 21.566-15.207 0-9.123-5.531-13.547-22.118-19.629-26.266-8.847-37.049-23.225-37.049-39.26 0-23.777 19.631-42.024 50.87-42.024 14.93 0 27.927 3.594 35.944 8.018l-6.913 23.777a59.483 59.483 0 0 0-28.476-7.741c-12.166 0-18.8 6.083-18.8 14.376 0 8.571 6.361 12.442 23.5 18.8 24.607 8.57 35.667 21.012 35.944 40.918 0 24.607-19.078 42.3-54.743 42.3-16.311 0-30.967-3.871-40.917-9.4Z"
							data-name="Path 65"
						/>
					</g>
				</g>
				<g data-name="Group 37">
					<g
						clipPath="url(#CosmosMobileSvgc)"
						data-name="Group 36"
						transform="translate(765.444 299.528)"
					>
						<path
							className={pathClasses}
							d="M-3.359 0h58.591v73.513H-3.359z"
							data-name="Rectangle 74"
						/>
					</g>
				</g>
				<g data-name="Group 39">
					<g
						clipPath="url(#CosmosMobileSvgd)"
						data-name="Group 38"
						transform="translate(867.477 204.15)"
					>
						<path
							className={pathClasses}
							d="M-7.727-.001h99.635v88.183H-7.727z"
							data-name="Rectangle 75"
						/>
					</g>
				</g>
				<g data-name="Group 41">
					<g
						clipPath="url(#CosmosMobileSvge)"
						data-name="Group 40"
						transform="translate(821.732 167.01)"
					>
						<path
							className={pathClasses}
							d="M-7.502 0h61.339v34.021H-7.502z"
							data-name="Rectangle 76"
						/>
					</g>
				</g>
				<g data-name="Group 43">
					<g
						clipPath="url(#CosmosMobileSvgf)"
						data-name="Group 42"
						transform="translate(844.536 166.827)"
					>
						<path
							className={pathClasses}
							d="M-21.707 6.192 86.356-24.634 115.09 76.097 7.027 106.923z"
							data-name="Rectangle 77"
						/>
					</g>
				</g>
				<g data-name="Group 45">
					<g
						clipPath="url(#CosmosMobileSvgg)"
						data-name="Group 44"
						transform="translate(798.675 228.333)"
					>
						<path
							className={pathClasses}
							d="M-31.306 0h193.907v154.181H-31.306z"
							data-name="Rectangle 78"
						/>
					</g>
				</g>
				<g data-name="Group 47">
					<g
						clipPath="url(#CosmosMobileSvgh)"
						data-name="Group 46"
						transform="translate(742.44 178.987)"
					>
						<path
							className={pathClasses}
							d="M-46.35 13.045 98.886-28.384l53.83 188.708L7.48 201.753z"
							data-name="Rectangle 79"
						/>
					</g>
				</g>
			</g>
		</svg>
	);
};

export const ChevronDown: React.FC<IStylableSvg> = ({ pathClasses, svgClasses }) => {
	return (
		<svg width={9} height={8} data-name="Layer 2" viewBox="0 0 7.31 6.27" className={svgClasses}>
			<path
				d="M4.21 6.27h-.98L0 .87l1.26-.8 1.95 3.14.49 1.27.43-1.26L6.05 0l1.26.8-3.09 5.47Z"
				data-name="Nav/Foot"
				className={pathClasses}
			/>
		</svg>
	);
};
type NavLogoMetadataType = {
	[id in NavLogoKind]: INavLogoKindMetadatum;
};
interface INavLogoKindMetadatum {
	readonly LogoSvg: React.FC<IStylableSvg>;
	readonly href: string;
}
export const NavLogoKindMetadata: NavLogoMetadataType = {
	[NavLogoKind.Epic]: { LogoSvg: EpicSvg, href: "https://www.epic.com" },
	[NavLogoKind.EpicShare]: {
		LogoSvg: EpicShareSvg,
		href: "https://www.epicshare.org",
	},
	[NavLogoKind.EpicResearch]: {
		LogoSvg: EpicResearchSvg,
		href: "https://www.epicresearch.org",
	},
	[NavLogoKind.MyChart]: {
		LogoSvg: MyChartSvg,
		href: "https://mychart.org",
	},
	[NavLogoKind.UserWeb]: {
		LogoSvg: UserWebSvg,
		href: "https://userweb.epic.com",
	},
	[NavLogoKind.OpenDotEpic]: {
		LogoSvg: OpenDotEpic,
		href: "https://open.epic.com",
	},
	[NavLogoKind.Showroom]: {
		LogoSvg: Showroom,
		href: "https://showroom.epic.com",
	},
	[NavLogoKind.ConnectionHub]: {
		LogoSvg: ConnectionHub,
		href: "https://fhir.epic.com/ConnectionHub",
	},
	[NavLogoKind.GetConnected]: {
		LogoSvg: GetConnected,
		href: "",
	},
	[NavLogoKind.Cosmos]: {
		LogoSvg: CosmosSvg,
		href: "https://cosmos.epic.com/",
	},
};
